import { useRef, useState } from "react";
import { Download, Eraser, FileUp, Printer, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, errorText, trackDownload } from "@/lib/api";
import { printTools } from "@/lib/scroll";
import { useStoredState } from "@/hooks/useStoredState";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { TextField, AutoTextarea, Pills } from "@/components/tools/fields";
import { SelectField, ErrorText } from "@/components/rc/shared";
import { FullFooter } from "@/components/workbook/PackPage";
import { SCHOOL_TYPES, REGIONS, COUNTS, PROFILE, CPD, SIGNOFF, milestones, scrub } from "@/data/toolkit4";

const KEY = "t4-planner-v1";
const INIT = { type: "", region: "", county: "", role: "", track: "12", signoff: "", ofsted: "", cqc: "", ...Object.fromEntries(COUNTS.map(([k]) => [k, "0"])),
  ...Object.fromEntries(PROFILE.map(([k]) => [k, ""])), ...Object.fromEntries(CPD.map(([k]) => [k, ""])) };

const Card = ({ title, children, testid }) => (
  <section className="avoid-break rounded-[1.75rem] border-2 border-line bg-paper p-5 sm:p-8" data-testid={testid}>
    <h2 className="mb-5 font-display text-2xl font-medium sm:text-3xl">{title}</h2>{children}
  </section>
);

const Upload = ({ onText }) => {
  const ref = useRef(null);
  const load = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!/\.(txt|md)$/i.test(file.name)) { toast.error("Please upload a .txt file, or copy the text from the PDF and paste it below."); return; }
    const reader = new FileReader();
    reader.onload = () => { onText(scrub(String(reader.result || ""))); toast.success("Loaded and pseudonymised in your browser"); };
    reader.readAsText(file);
  };
  return (
    <label className="btn btn-ghost btn-sm cursor-pointer"><FileUp size={14} aria-hidden /> Load report text (.txt)
      <input ref={ref} type="file" accept=".txt,.md,text/plain" onChange={load} className="sr-only" data-testid="t4-upload" />
    </label>
  );
};

const Plan = ({ d }) => (
  <div className="space-y-3" data-testid="t4-milestones">
    {milestones(d.track, d).map(([t, items]) => (
      <div key={t} className="avoid-break rounded-2xl border-l-4 border-brand bg-surface p-4">
        <p className="font-bold">{t}</p>
        <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">{items.map((i) => <li key={i}>{i}</li>)}</ul>
      </div>
    ))}
  </div>
);

const Ratio = ({ d }) => {
  const sen = Number(d.sen) || 0;
  const ehcp = Number(d.ehcp) || 0;
  const share = sen + ehcp ? Math.round((ehcp / (sen + ehcp)) * 100) : 0;
  return <p className="mt-4 text-sm font-bold" data-testid="t4-ratio">EHCPs make up {share}% of pupils with SEND in your cohort. Compare this with your local authority's figures to check provision matches need.</p>;
};

const FeedbackGate = ({ d }) => {
  const [f, setF] = useState({ feedback: "", school: "", email: "", pilot: false });
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const printPlan = () => { trackDownload("toolkit-4-plan"); printTools("page"); };
  const download = async () => {
    if (f.feedback.trim().length < 5) return setError("Please share a short piece of feedback first. It's the only thing we ask in return for this free tool.");
    setError("");
    try {
      await api.post("/feedback", { pack: "toolkit-4", category: f.pilot ? "Feature request" : "Workbook feedback", improve: f.feedback, role: d.role.slice(0, 80),
        school: f.school, email: f.email || null, founding: f.pilot ? "Tell me more" : "", resources_requested: f.pilot ? "School Improvement Pilot Program opt-in" : "" });
    } catch (err) {
      return setError(errorText(err));
    }
    setSent(true);
    return undefined;
  };
  if (sent) {
    return (
      <Card title="Thank you. Your downloads are ready" testid="t4-feedback">
        <p className="mb-5 text-ink-soft" data-testid="t4-ready">Print or save your personalised plan, or download the blank planner to complete by hand.</p>
        <div className="flex flex-wrap gap-3">
          <button type="button" onClick={printPlan} className="btn btn-brand" data-testid="t4-print-plan"><Printer size={16} aria-hidden /> Print or save my plan (PDF)</button>
          <a href="/pdfs/school-improvement-turnaround-planner.pdf" download onClick={() => trackDownload("toolkit-4-pdf")} className="btn btn-ghost" data-testid="t4-download-blank"><Download size={16} aria-hidden /> Download blank planner (PDF)</a>
        </div>
        <p className="mt-3 text-sm text-ink-soft">To save your plan as a file, choose "Save as PDF" in the print window.</p>
      </Card>
    );
  }
  return (
    <Card title="Feedback and download" testid="t4-feedback">
      <AutoTextarea id="t4-fb" label="Your feedback on this planner (required)" rows={3} value={f.feedback} onChange={(v) => setF({ ...f, feedback: v })} testid="t4-feedback-text" />
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <TextField id="t4-school" label="School name (optional)" value={f.school} onChange={(v) => setF({ ...f, school: v })} testid="t4-school" />
        <TextField id="t4-email" label="Email (optional)" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="t4-email" />
      </div>
      <label className="mt-4 flex items-start gap-3 font-bold"><input type="checkbox" checked={f.pilot} onChange={(e) => setF({ ...f, pilot: e.target.checked })} className="mt-1 h-5 w-5 accent-[var(--brand)]" data-testid="t4-pilot" />
        Opt in to the School Improvement Pilot Program (tailored toolkits, improvement dashboards and governance reporting).</label>
      <ErrorText error={error} testid="t4-error" />
      <button type="button" onClick={download} className="btn btn-brand mt-5" data-testid="t4-download"><Download size={16} aria-hidden /> Send feedback and get the PDF</button>
    </Card>
  );
};

export default function Toolkit4Page() {
  const [d, setD, reset] = useStoredState(KEY, INIT);
  const set = (k) => (v) => setD({ ...d, [k]: v });
  const purge = () => { if (window.confirm("Clear everything in this planner? Other toolkits are not affected.")) reset(); };
  return (
    <>
      <TopBar minimal />
      <main id="main" tabIndex={-1} className="outline-none">
        <div className="print-scope mx-auto max-w-[1100px] space-y-6 px-5 pb-20 pt-[150px] sm:px-8 lg:pt-[178px]" data-pack="toolkit-4">
          <header>
            <p className="eyebrow mb-4">Toolkit 4 · Whole-school improvement · Free · No sign-up</p>
            <h1 className="font-display text-[2.6rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl">School Improvement &amp; <span className="italic text-brand">Turnaround Planner</span></h1>
            <p className="mt-5 max-w-2xl text-lg text-ink-soft">Whole-school improvement planning, not just SEND. Turn Ofsted and area SEND (Ofsted and CQC) findings and your school profile into a 6- or 12-month plan with CPD tracks and governance sign-off.</p>
            <p className="mt-4 flex items-start gap-2 text-sm font-bold"><ShieldCheck size={18} className="mt-0.5 shrink-0 text-brand" aria-hidden />Everything stays in your browser. Names, postcodes, dates, emails and UPNs are removed automatically from pasted text. Always check before printing.</p>
          </header>
          <Card title="1. School profile" testid="t4-profile">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <SelectField id="t4-type" label="School type" value={d.type} onChange={set("type")} options={SCHOOL_TYPES} testid="t4-type" />
              <SelectField id="t4-region" label="Region" value={d.region} onChange={set("region")} options={REGIONS} testid="t4-region" />
              <TextField id="t4-county" label="County or local authority (optional)" value={d.county} onChange={set("county")} testid="t4-county" />
              <TextField id="t4-role" label="Your role (optional)" value={d.role} onChange={set("role")} testid="t4-role" />
            </div>
            <div className="mt-4 grid grid-cols-2 gap-4 lg:grid-cols-4">
              {COUNTS.map(([k, label]) => <TextField key={k} id={`t4-${k}`} label={label} type="number" min={0} value={d[k]} onChange={set(k)} testid={`t4-${k}`} />)}
            </div>
            <Ratio d={d} />
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {PROFILE.map(([k, label, opts]) => <SelectField key={k} id={`t4-${k}`} label={label} value={d[k]} onChange={set(k)} options={opts} testid={`t4-${k}`} />)}
            </div>
          </Card>
          <Card title="2. Inspection findings" testid="t4-findings">
            <div className="mb-4"><Upload onText={(t) => set("ofsted")(t)} /></div>
            <div className="grid gap-4 md:grid-cols-2">
              <AutoTextarea id="t4-ofsted" label="Ofsted areas for improvement (whole school)" rows={6} value={d.ofsted} onChange={(v) => set("ofsted")(scrub(v))} testid="t4-ofsted" />
              <AutoTextarea id="t4-cqc" label="Area SEND (Ofsted and CQC) findings" rows={6} value={d.cqc} onChange={(v) => set("cqc")(scrub(v))} testid="t4-cqc" />
            </div>
          </Card>
          <Card title="3. Improvement timeline" testid="t4-timeline">
            <Pills legend="Plan length" hideLegend={false} name="t4-track" value={d.track} onChange={set("track")} testid="t4-track"
              options={[{ v: "6", label: "6-month rapid stabilisation" }, { v: "12", label: "12-month systemic culture shift" }]} />
            <div className="mt-5"><Plan d={d} /></div>
            <div className="mt-5 max-w-sm"><SelectField id="t4-signoff" label="Governance sign-off" value={d.signoff} onChange={set("signoff")} options={SIGNOFF[d.track]} testid="t4-signoff" /></div>
          </Card>
          <Card title="4. Staff training and CPD tracks" testid="t4-cpd">
            <div className="grid gap-4 md:grid-cols-3">
              {CPD.map(([k, label, hint]) => <div key={k}><AutoTextarea id={`t4-${k}`} label={label} rows={4} value={d[k]} onChange={set(k)} testid={`t4-${k}`} /><p className="mt-1 text-xs text-ink-soft">{hint}</p></div>)}
            </div>
          </Card>
          <div className="no-print space-y-6">
            <FeedbackGate d={d} />
            <button type="button" onClick={purge} className="btn btn-ghost" data-testid="t4-purge"><Eraser size={16} aria-hidden /> Clear this planner</button>
          </div>
          <div className="rounded-2xl border-t-2 border-brand pt-4 text-ink-soft" data-testid="t4-company-footer"><FullFooter /></div>
        </div>
      </main>
      <Footer />
    </>
  );
}
